import React, { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { PropertyGridProps } from '../../interfaces';
import PropertyCard from './PropertyCard';


const getCols = (w: number) => (w < 640 ? 1 : w < 1024 ? 2 : 4);

const PropertyGrid = ({ items, hasMore, fetchMore, className = '' }: PropertyGridProps) => {
  const [cols, setCols] = useState(() => getCols(window.innerWidth));
  useEffect(() => {
    const onResize = () => setCols(getCols(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* virtualiser – counts rows, not cards */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rowCount = Math.ceil(items.length / cols) + (hasMore ? 1 : 0);
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 150,
    overscan: 2,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  /* infinite‑scroll trigger */
  const loadingRef = useRef(false);
  const lastRow = virtualRows[virtualRows.length - 1];
  const scrollEl = rowVirtualizer.scrollElement;
  const shouldLoadMore =
    hasMore &&
    lastRow &&
    lastRow.index === rowCount - 1 &&
    lastRow.start < (scrollEl?.scrollTop ?? 0) + (scrollEl?.clientHeight ?? 0);

  useEffect(() => {
    if (!shouldLoadMore || loadingRef.current) return;
    loadingRef.current = true;
    Promise.resolve(fetchMore()).finally(() => (loadingRef.current = false));
  }, [shouldLoadMore, fetchMore]);

  /* custom scrollbar bookkeeping (untouched) */
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const content = scrollRef.current!;
    const track = trackRef.current!;
    const thumb = thumbRef.current!;
    let dragging = false;
    let startY = 0,
      startScroll = 0;

    track.style.display = items.length ? 'block' : 'none';

    const updateThumb = () => {
      const ratio = content.clientHeight / content.scrollHeight;
      const thumbH = Math.max(ratio * content.clientHeight, 30);
      const maxScroll = content.scrollHeight - content.clientHeight;
      const trackSpace = content.clientHeight - thumbH;
      const top = (content.scrollTop / maxScroll) * trackSpace || 0;
      thumb.style.height = `${thumbH}px`;
      thumb.style.top = `${top}px`;
    };
    updateThumb();
    content.addEventListener('scroll', updateThumb);
    window.addEventListener('resize', updateThumb);

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      startY = e.clientY;
      startScroll = content.scrollTop;
      thumb.setPointerCapture(e.pointerId);
      document.body.style.userSelect = 'none';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      const delta = e.clientY - startY;
      const maxScroll = content.scrollHeight - content.clientHeight;
      const trackSpace = content.clientHeight - thumb.clientHeight;
      content.scrollTop = startScroll + (delta * maxScroll) / trackSpace;
    };
    const onPointerUp = (e: PointerEvent) => {
      dragging = false;
      thumb.releasePointerCapture(e.pointerId);
      document.body.style.userSelect = '';
    };
    thumb.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      content.removeEventListener('scroll', updateThumb);
      window.removeEventListener('resize', updateThumb);
      thumb.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [items]);

  /* row renderer */
  const renderRow = (vRow: (typeof virtualRows)[number]) => {
    if (hasMore && vRow.index === rowCount - 1) {
      return (
        <div
          key="loader"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `translateY(${vRow.start}px)`,
            width: '100%',
            height: vRow.size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <h2 style={{ color: 'gray' }}>Loading…</h2>
        </div>
      );
    }

    const cells: JSX.Element[] = [];
    for (let c = 0; c < cols; c++) {
      const idx = vRow.index * cols + c;
      if (idx >= items.length) break;
      cells.push(<PropertyCard key={idx} data={items[idx]} />);
    }

    return (
      <div
        key={vRow.index}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `translateY(${vRow.start}px)`,
          width: '100%',
          height: vRow.size,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: 8,
            height: '100%',
          }}
        >
          {cells}
        </div>
      </div>
    );
  };

  return (
    <div className={`scroll-container ${className}`}>
      <div ref={scrollRef} className="scroll-content">
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: 'relative',
            marginRight: 20,
          }}
        >
          {virtualRows.map(renderRow)}
        </div>
      </div>

      <div ref={trackRef} className="custom-scrollbar">
        <div ref={thumbRef} className="thumb" />
      </div>
    </div>
  );
};

export default PropertyGrid;
